import { Suspense } from 'react';
import { isNotFound } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { usePortalCampaignQuery, usePortalMetadataQuery } from '@/lib/queries-portal';
import { categories, type CategoryKey, type Category } from '@worldcoo/shared/categories';
import { categoryMessages } from '@/lib/portal-messages';
import { DivWithPrimaryColorVariable } from './div-with-primary-color-variable';
import { useCampaignDisplayContext } from './campaign-display-context';

const categoryNumberToKey = Object.fromEntries(
	Object.entries(categories).map(([key, value]) => [value, key]),
) as Record<Category, CategoryKey>;

export function CampaignMetadata(props: { campaignId: string }) {
	const displayData = useCampaignDisplayContext();
	if (displayData) {
		const { campaign, metadata } = displayData;
		return (
			<CampaignMetadataContent
				categories={campaign.categories}
				cityName={campaign.cityName}
				startDate={campaign.startDate}
				endDate={campaign.endDate}
				showCategory={metadata.showCampaignCategory}
				showCity={metadata.showCampaignCity}
				showDates={metadata.showCampaignDates}
			/>
		);
	}
	return (
		<Suspense>
			<SuspensedCampaignMetadata campaignId={props.campaignId} />
		</Suspense>
	);
}

function SuspensedCampaignMetadata(props: { campaignId: string }) {
	const campaign = usePortalCampaignQuery(props.campaignId);
	const metadata = usePortalMetadataQuery();

	if (isNotFound(campaign) || isNotFound(metadata)) return null;

	return (
		<CampaignMetadataContent
			categories={campaign.data.categories}
			cityName={campaign.data.cityName}
			startDate={campaign.data.startDate}
			endDate={campaign.data.endDate}
			showCategory={metadata.showCampaignCategory}
			showCity={metadata.showCampaignCity}
			showDates={metadata.showCampaignDates}
		/>
	);
}

function CampaignMetadataContent(props: {
	categories: number[];
	cityName?: string;
	startDate?: string;
	endDate?: string;
	showCategory: boolean;
	showCity: boolean;
	showDates: boolean;
}) {
	const { t } = useLingui();
	const categoryLabels = props.showCategory
		? props.categories
				.map((cat) => categoryNumberToKey[cat as Category])
				.filter(Boolean)
				.map((key) => (categoryMessages[key] ? t(categoryMessages[key]) : key))
		: [];

	const hasCity = props.showCity && !!props.cityName;
	const hasDates = props.showDates && (!!props.startDate || !!props.endDate);

	const hasAnyInfo = categoryLabels.length > 0 || hasCity || hasDates;
	if (!hasAnyInfo) return null;

	return (
		<DivWithPrimaryColorVariable className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
			{categoryLabels.length > 0 && (
				<span className="flex items-center gap-1.5">
					<CategoryIcon className="text-primary shrink-0" />
					{categoryLabels.join(', ')}
				</span>
			)}

			{hasCity && (
				<span className="flex items-center gap-1.5">
					<CityIcon className="text-primary shrink-0" />
					{props.cityName}
				</span>
			)}

			{hasDates && <DateRange startDate={props.startDate} endDate={props.endDate} />}
		</DivWithPrimaryColorVariable>
	);
}

const formatDate = new Intl.DateTimeFormat('en-GB', {
	day: '2-digit',
	month: '2-digit',
	year: '2-digit',
	timeZone: 'Europe/Madrid',
});

function DateRange(props: { startDate: string | undefined; endDate: string | undefined }) {
	const { t } = useLingui();

	return (
		<span className="flex items-center gap-1.5">
			<CalendarIcon className="text-primary shrink-0" />
			{props.startDate && (
				<>
					{t`From:`} <strong>{formatDate.format(new Date(props.startDate))}</strong>
				</>
			)}
			{props.endDate && (
				<>
					{t`Until:`} <strong>{formatDate.format(new Date(props.endDate))}</strong>
				</>
			)}
		</span>
	);
}

function CategoryIcon(props: React.SVGProps<SVGSVGElement>) {
	return (
		<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" {...props} viewBox="0 0 24 24">
			<path
				stroke="currentColor"
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth="1.5"
				d="M9.159 10.87c-.1-.01-.22-.01-.33 0a4.42 4.42 0 0 1-4.27-4.43c0-2.45 1.98-4.44 4.44-4.44a4.435 4.435 0 0 1 .16 8.87"
			/>
			<path
				stroke="currentColor"
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth="1.5"
				d="M16.411 4c1.94 0 3.5 1.57 3.5 3.5 0 1.89-1.5 3.43-3.37 3.5a1 1 0 0 0-.26 0"
				opacity=".4"
			/>
			<path
				stroke="currentColor"
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth="1.5"
				d="M4.159 14.56c-2.42 1.62-2.42 4.26 0 5.87 2.75 1.84 7.26 1.84 10.01 0 2.42-1.62 2.42-4.26 0-5.87-2.74-1.83-7.25-1.83-10.01 0"
			/>
			<path
				stroke="currentColor"
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth="1.5"
				d="M18.34 20c.72-.15 1.4-.44 1.96-.87 1.56-1.17 1.56-3.1 0-4.27-.55-.42-1.22-.7-1.93-.86"
				opacity=".4"
			/>
		</svg>
	);
}

function CityIcon(props: React.SVGProps<SVGSVGElement>) {
	return (
		<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" {...props} viewBox="0 0 24 24">
			<path
				stroke="currentColor"
				strokeWidth="1.5"
				d="M10.999 13.43a3.12 3.12 0 1 0 0-6.24 3.12 3.12 0 0 0 0 6.24Z"
				opacity=".4"
			/>
			<path
				stroke="currentColor"
				strokeWidth="1.5"
				d="M2.622 8.49c1.97-8.66 14.8-8.65 16.76.01 1.15 5.08-2.01 9.38-4.78 12.04a5.194 5.194 0 0 1-7.21 0c-2.76-2.66-5.92-6.97-4.77-12.05Z"
			/>
		</svg>
	);
}

function CalendarIcon(props: React.SVGProps<SVGSVGElement>) {
	return (
		<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" {...props} viewBox="0 0 24 24">
			<path
				stroke="currentColor"
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeMiterlimit="10"
				strokeWidth="1.5"
				d="M7 2v3m8-3v3"
			/>
			<path
				stroke="currentColor"
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeMiterlimit="10"
				strokeWidth="1.5"
				d="M2.5 9.09h17"
				opacity=".4"
			/>
			<path
				stroke="currentColor"
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeMiterlimit="10"
				strokeWidth="1.5"
				d="M20 8.5V17c0 3-1.5 5-5 5H7c-3.5 0-5-2-5-5V8.5c0-3 1.5-5 5-5h8c3.5 0 5 2 5 5"
			/>
			<path
				stroke="currentColor"
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeWidth="2"
				d="M14.694 13.7h.009m-.009 3h.009m-3.708-3h.009m-.009 3h.009m-3.709-3h.01m-.012 3h.009"
				opacity=".4"
			/>
		</svg>
	);
}
